'use strict';

import { sharedStorage } from '../utils/sharedStorage';
import { tabIdentity } from '../utils/tabIdentity';

/**
 * Cross-tab live inventory for multi-cart (presence probe).
 *
 * **Purpose:** Discover which storefront tabs are still alive and what page
 * context they report.
 *
 * **Scope:**
 * - Page context from the current DOM/URL (`getPageContext`)
 * - Presence probes (`collectTabContexts`, `collectLiveTabs`, `pendingProbes`)
 *
 * **Out of scope:** Bus subscribe (global `components/coordinator`); Server
 * `Tab-Reconcile` scheduler (`tabReconcile`); Checkout Ownership UX / lease
 * (`checkoutOwnership`); Save Execution Gate; CSRF; title attention
 * (`tabAttention`).
 */
export const tabPresence = {
  /**
   * @readonly
   * @private
   */
  STATE_KEY: 'presence',

  /**
   * @private
   *
   * @returns {{
   *   initialized: boolean,
   *   pendingProbes: Record<string, Object>
   * }}
   */
  get state() {
    return sharedStorage.get(this.STATE_KEY, () => ({
      initialized: false,
      pendingProbes: {}
    }));
  },

  /**
   * Whether peer bus handlers for this module have been registered.
   *
   * @returns {boolean}
   */
  isInitialized() {
    return !!this.state.initialized;
  },

  /**
   * Mark peer bus handlers as registered (idempotent wirer guard).
   */
  setInitialized() {
    this.state.initialized = true;
  },

  /**
   * DOM-derived page context.
   *
   * @param {string|null} tabId
   *
   * @returns {{ tabId: string, action: string, params: Record<string, string> }|null}
   */
  getPageContext(tabId) {
    if (!tabId) {
      return null;
    }

    const page = document.querySelector('.page');
    const action = (page && page.getAttribute('data-action')) || '';
    const params = this.parseQueryParams(
      (page && page.getAttribute('data-querystring')) || ''
    );

    return {
      tabId: tabId,
      action: action,
      params: params
    };
  },

  /**
   * Presence contexts from open tabs that answer the probe.
   *
   * @param {number} [timeout=400]
   *
   * @returns {Promise<Array<{ tabId: string, action: string, params: Record<string, string> }>>}
   */
  collectTabContexts(timeout = 400) {
    const probeNonce = Math.random().toString(36).slice(2);
    const contexts = {};
    const selfTabId = tabIdentity.peekTabId();
    const selfContext = this.getPageContext(selfTabId);

    if (selfContext) {
      contexts[selfContext.tabId] = selfContext;
    }

    this.state.pendingProbes[probeNonce] = contexts;
    sharedStorage.tabChannel.publish('presence', { probeNonce: probeNonce });

    return new Promise(resolve => {
      window.setTimeout(() => {
        delete this.state.pendingProbes[probeNonce];
        resolve(Object.keys(contexts).map(tabId => contexts[tabId]));
      }, timeout);
    });
  },

  /**
   * Probes all open storefront tabs that have started the tab channel.
   * Only tabs that answer this probe are returned.
   *
   * @param {number} [timeout=400]
   *
   * @returns {Promise<string[]>}
   */
  collectLiveTabs(timeout = 400) {
    return this.collectTabContexts(timeout).then(contexts => contexts.map(
      context => context.tabId
    ));
  },

  /**
   * Reply to a presence probe with this tab's page context.
   *
   * @param {{ probeNonce: string }} [payload]
   */
  onPresence(payload = {}) {
    const context = this.getPageContext(tabIdentity.peekTabId());

    if (context) {
      sharedStorage.tabChannel.publish('alive', Object.assign({
        probeNonce: payload.probeNonce
      }, context));
    }
  },

  /**
   * Collect an alive reply into the matching pending probe map.
   *
   * @param {{
   *   probeNonce: string,
   *   tabId: string,
   *   action?: string,
   *   params?: Record<string, string>
   * }} [payload]
   */
  onAlive(payload = {}) {
    if (!payload.probeNonce || !payload.tabId) {
      return;
    }

    const probe = this.state.pendingProbes[payload.probeNonce];

    if (probe) {
      probe[payload.tabId] = {
        tabId: payload.tabId,
        action: payload.action || '',
        params: payload.params && typeof payload.params === 'object' ? payload.params : {}
      };
    }
  },

  /**
   * Parse `.page[data-querystring]` into a plain object.
   *
   * @private
   *
   * @param {string} querystring
   *
   * @returns {Record<string, string>}
   */
  parseQueryParams(querystring) {
    const params = {};

    if (!querystring || typeof querystring !== 'string') {
      return params;
    }

    try {
      const search = querystring.charAt(0) === '?' ? querystring.slice(1) : querystring;

      new URLSearchParams(search).forEach((value, key) => {
        params[key] = value;
      });
    } catch (e) {
      // Malformed querystring — treat as empty params.
    }

    return params;
  }
};
